import { CopyContent, HTML_TYPE, TEXT_TYPE } from '../../../../common/copy'

import { AiResult, FormattedSummaryResult, SegmentedResult, StructuredSummaryResult } from '../../../../models/AiResult'
import { AiFeature } from '../../../../models/AiFeature'
import { Task } from '../../../../models/MeetingSupport'
import { User } from '../../../../models/User'

import { getEditorStateFromText, getHtmlContentFromState } from './MeetingResults/Lexical/utils'
import { FormattedResultContentType, NO_RESULT_TEXT } from './MeetingResults/common'
import { CUSTOM_PRESET_MAPPING } from './CustomPresets/CustomPresetMapping'
import { getResultSegmentHeader, getSafeValueOnPath } from './utils'

interface KeyObject {
  type: 'key'
  object: string
}

interface ValueObject {
  type: 'value'
  object: any
  path: string
  displayAsList?: boolean
}

interface EmptyObject {
  type: 'empty'
  object: null
}

type ResultObject = KeyObject | ValueObject | EmptyObject

interface KeyValue {
  type: 'key'
  key: string
}

interface LeafValue {
  type: 'leaf'
  path: string
  displayAsList?: boolean
}

interface EmptyValue {
  type: 'empty'
}

export type ResultValue = KeyValue | LeafValue | EmptyValue

export interface CopyData {
  groupedTasks?: { user: User; tasks: Task[] }[]
  roomSessionStartedAt?: string
}

export const getEmptyResultCopyContent = (): CopyContent => {
  return {
    [TEXT_TYPE]: NO_RESULT_TEXT,
    [HTML_TYPE]: getHtmlContentFromText(NO_RESULT_TEXT),
  }
}

export const getEmptyCopyContent = (): CopyContent => {
  return {
    [TEXT_TYPE]: '',
    [HTML_TYPE]: '',
  }
}

export const copyResult = (aiFeature: AiFeature, aiResult: AiResult, copyData?: CopyData): CopyContent => {
  const { preset: { id: aiFeaturePresetId } = {} } = aiFeature
  const customCopy = aiFeaturePresetId ? CUSTOM_PRESET_MAPPING[aiFeaturePresetId]?.copyResult : null
  if (customCopy) {
    return customCopy(aiFeature, aiResult, copyData)
  }

  if (aiResult.result?.type === 'segmented') {
    return copySegmentedResult(aiFeature, aiResult, copyData)
  }

  if (aiResult.result?.type === 'structuredSummary') {
    return copyFullStructuredSummaryResult(aiFeature, aiResult.result, copyData)
  }

  return getEmptyResultCopyContent()
}

export const copyPartialStructuredSummaryResult = (result: AiResult, rootPath: string = '$.result'): CopyContent => {
  if (result.result?.type !== 'structuredSummary') {
    return getEmptyResultCopyContent()
  }

  const {
    result: { formattedResults },
  } = result

  const resultOnPath = getSafeValueOnPath(result.result, rootPath, {})
  const resultValues = traverseResultKeysAndValues(resultOnPath)

  return getTextAndHtmlValuesForCopy(resultValues, resultOnPath, formattedResults)
}
export const copyFullStructuredSummaryResult = (aiFeature: AiFeature, result: StructuredSummaryResult, copyData?: CopyData): CopyContent => {
  const { result: structuredSummaryResult, formattedResults } = result

  const resultValues = traverseResultKeysAndValues(structuredSummaryResult, '$.result')

  return getTextAndHtmlValuesForCopy(resultValues, result, formattedResults)
}

export const copySegmentedResult = (aiFeature: AiFeature, aiResult: AiResult, copyData?: CopyData): CopyContent => {
  if (aiResult.result?.type !== 'segmented') {
    return getEmptyResultCopyContent()
  }

  const result = aiResult.result as SegmentedResult

  const { roomSessionStartedAt } = copyData ?? {}
  const { segmentResults } = result

  const segmentCount =
    Math.max(
      ...segmentResults.map((segmentResult) => {
        return segmentResult.metadata.segmentIdx
      }),
    ) + 1

  const textContents: string[] = []
  const htmlContents: string[] = []

  for (const segmentResult of segmentResults) {
    const { results, metadata } = segmentResult
    for (const result of results) {
      if (result.type !== 'structuredSummary') {
        continue
      }
      const copiedResult = copyFullStructuredSummaryResult(aiFeature, result, copyData)
      const segmentHeader = `${getResultSegmentHeader(metadata, roomSessionStartedAt)} - Segment: ${metadata.segmentIdx + 1} / ${segmentCount}`
      textContents.push(`\n${segmentHeader}\n`)
      textContents.push(copiedResult[TEXT_TYPE])
      htmlContents.push(`<h4>${segmentHeader}</h4>`)
      htmlContents.push(copiedResult[HTML_TYPE])
    }
  }

  return {
    [TEXT_TYPE]: textContents.join('\n'),
    [HTML_TYPE]: htmlContents.join(''),
  }
}

const getTextAndHtmlValuesForCopy = (
  resultValues: ResultValue[],
  result: any,
  formattedResults: {
    [p: string]: FormattedSummaryResult[]
  },
) => {
  const textContents: string[] = []
  const htmlContents: string[] = []

  for (const resultValue of resultValues) {
    const { type } = resultValue
    if (type === 'leaf') {
      const { path, displayAsList } = resultValue
      const textContent = getSafeValueOnPath(result, path, '') || NO_RESULT_TEXT
      const htmlContent = getHtmlContent(formattedResults, path, textContent, displayAsList)
      htmlContents.push(htmlContent)
      textContents.push(textContent)
    } else if (type === 'key') {
      const { key } = resultValue
      textContents.push(key)
      htmlContents.push(`<p><b>${key}</b></p>`)
    } else if (type === 'empty') {
      textContents.push(NO_RESULT_TEXT)
      htmlContents.push(getHtmlContentFromText(NO_RESULT_TEXT))
    }
  }

  return {
    [TEXT_TYPE]: textContents.join('\n'),
    [HTML_TYPE]: htmlContents.join(''),
  }
}
const traverseResultKeysAndValues = (obj: any, rootPath: string = '$') => {
  const stack: ResultObject[] = [{ type: 'value', object: obj, path: rootPath }]
  const result: ResultValue[] = []
  while (stack.length) {
    const current = stack.shift()
    if (!current) {
      break
    }
    const { object } = current
    if (current.type === 'key' || current.type === 'empty' || !result.some((r) => r.type === 'leaf' && r.path === current.path)) {
      if (current.type === 'key') {
        result.push({ type: 'key', key: object })
        continue
      } else if (current.type === 'empty') {
        result.push({ type: 'empty' })
        continue
      }

      if (!Array.isArray(object) && !(typeof object === 'object')) {
        result.push({ type: 'leaf', path: current.path, displayAsList: current.displayAsList })
        continue
      }

      if (Array.isArray(object)) {
        if (!object.length) {
          stack.unshift({ type: 'empty', object: null })
        } else {
          stack.unshift(
            ...object.map((o, idx) => {
              return { type: 'value', object: o, path: `${current.path}[${idx}]`, displayAsList: true } as ResultObject
            }),
          )
        }
      } else if (typeof object === 'object') {
        if (!object || !Object.keys(object).length) {
          stack.unshift({ type: 'empty', object: null })
        } else {
          stack.unshift(
            ...Object.entries(object)
              .map(([key, value]) => {
                return [
                  { type: 'key', object: key },
                  {
                    type: 'value',
                    object: value,
                    path: `${current.path}["${key}"]`,
                  },
                ] as ResultObject[]
              })
              .flat(),
          )
        }
      }
    }
  }
  return result
}
const getHtmlContent = (
  formattedResults: {
    [path: string]: FormattedSummaryResult[]
  },
  path: string,
  textContent: string,
  displayAsList: boolean = false,
) => {
  const formattedResultsForPath = formattedResults[path]
  if (!formattedResultsForPath) {
    return getHtmlContentFromText(textContent, displayAsList)
  }
  const lexicalResult = formattedResultsForPath.find((formattedResult) => formattedResult.contentType === FormattedResultContentType.LEXICAL)
  const htmlResult = formattedResultsForPath.find((formattedResult) => formattedResult.contentType === FormattedResultContentType.HTML)
  const htmlContent = lexicalResult ? getHtmlContentFromState(lexicalResult.content) : htmlResult?.content
  if (htmlContent) {
    return displayAsList ? wrapIntoList(htmlContent) : htmlContent
  }
  return getHtmlContentFromText(textContent, displayAsList)
}

const getHtmlContentFromText = (textContent: string, displayAsList: boolean = false) => {
  const editorState = getEditorStateFromText(textContent)
  const htmlContent = getHtmlContentFromState(JSON.stringify(editorState))
  return displayAsList ? wrapIntoList(htmlContent) : htmlContent
}

const wrapIntoList = (content: string) => `<ul><li>${content}</li></ul>`
